// ============================================================================
// Ask AI's Buildings part — the card's fields and the save (batch 3 of item
// 944; TCSD-0531 "D1. B+D", TCSD-0533 "D3. B", TCSD-0535 "D5. A").
//
// One tool (propose_building_action) names a building action; the server
// (supabase/functions/am-copilot/buildings.ts + prepareBuildingAction) checks
// it the way the database would and fills in what the card shows. This file is
// the app half:
//   • BUILDING_CARD — for each action, the card's title and the fields it shows,
//     in the SAME words the building screen uses, plus the read-back sentence
//     and button words on the cards that move money or cannot be undone.
//   • commitBuildingAction — Do it. Every action saves through the function the
//     building SCREEN calls (TCSD-0188: one door per change). Nothing here
//     writes a table the screen does not, and nothing writes it differently.
//
// Two actions have no screen (Ask AI is their only door, recon 2026-10-20):
// closing feedback and the communication log. Their saves are the ones Ask AI
// already made, moved here unchanged.
// ============================================================================

import { supabase } from '../../shared/supabase';
import { rpcErrorMessage } from '../../shared/rpc-error';
import { errorMessage } from '../../shared/errors';
import { saveFieldsOnce } from '../account-management/hooks/use-facility-field-update';
import {
  addExistingContact,
  assignFacilityAm,
  contactSwapPlan,
  createContactForFacility,
  mergeContactInto,
  patchContactAssignment,
  removeContactAssignment,
  saveFeedback,
  saveRag,
  setPrimaryContact,
  summaryColumnsPatch,
  swapContactOrder,
  terminationStep,
} from '../account-management/lib/building-saves';
import {
  DETAIL_FIELD_SPECS,
  planDetailsPatch,
  saveFacilityDetailsFields,
} from '../account-management/lib/facility-details';
import { fileFacilityDocument } from '../account-management/components/facility-documents-card';
import { DOCUMENT_TYPES } from '../account-management/components/facility-documents-card';
import {
  MANAGED_BY_CHOICES,
  addFacilityChemical,
  removeFacilityChemical,
  updateFacilityChemical,
  type ChemicalManagedBy,
} from '../../shared/buildings/facility-chemicals';
import { reportCoordVerification } from '../../shared/map/coord-verify-control';
import { localToday } from '../../shared/format';
// The button's words: one copy, read here and by Ask AI's reply (card fixes, item 944).
import { buildingButtonWords, dayWords } from '../../../supabase/functions/am-copilot/buttons';

export const BUILDING_ACTIONS = [
  'log_feedback',
  'close_feedback',
  'log_communication',
  'set_rag',
  'edit_details',
  'edit_access',
  'change_account_manager',
  'acknowledge_termination',
  'approve_termination',
  'set_termination_final_date',
  'cancel_termination',
  'add_contact',
  'edit_contact',
  'set_contact_display',
  'move_contact',
  'remove_contact',
  'merge_contacts',
  'add_document',
  'add_chemical',
  'edit_chemical',
  'remove_chemical',
  'confirm_map_pin',
] as const;
export type BuildingAction = (typeof BUILDING_ACTIONS)[number];

/** Square footage stays out of Ask AI (Ben 2026-10-21) — the server's SQFT_FIELDS. */
export const SQFT_KEYS: readonly string[] = ['total_sqft', 'sterile_sqft', 'treatment_floor_sqft'];

/** A draft as the server persisted it (prepareBuildingAction). */
export interface BuildingDraft {
  facility_id: string;
  action: BuildingAction;
  fields: Record<string, unknown>;
  facility_name?: string | null;
  display_name?: string | null;
  account_id?: string | null;
  context?: Record<string, unknown>;
  options?: Record<string, string[]>;
}

export type FieldKind = 'text' | 'textarea' | 'select' | 'date' | 'datetime' | 'number' | 'boolean';
export interface FieldSpec {
  key: string;
  label: string;
  kind: FieldKind;
  options?: Array<{ value: string; label: string }>;
  required?: boolean;
  /** "Now …" — what the building holds today, shown under the field. */
  now?: string | null;
}
export interface CardSpec {
  title: string;
  fields: FieldSpec[];
  /** Plain lines the card shows above the fields (what is being changed). */
  info: Array<{ label: string; value: string }>;
  /** A line said in words on a card that moves money or cannot be undone (D3 B). */
  readBack: string | null;
  /** The button's words when they name the consequence; otherwise "Do it". */
  doItLabel: string | null;
  /** Rows to tick (close feedback). */
  rows?: Array<{ id: string; text: string }>;
  rowsKey?: string;
  /** Why Do it is not ready yet, in the screen's words. */
  missing: string | null;
}

// ── Words ────────────────────────────────────────────────────────────────────

const ctxOf = (d: BuildingDraft) => (d.context ?? {}) as Record<string, unknown>;
const str = (v: unknown) => (typeof v === 'string' ? v : v == null ? '' : String(v));
const blank = (v: unknown) => v == null || (typeof v === 'string' && v.trim() === '');
const opt = (values: readonly string[], labels?: Record<string, string>) => values.map((v) => ({ value: v, label: labels?.[v] ?? v.replace(/_/g, ' ') }));
const fromDraft = (d: BuildingDraft, key: string, fallback: string[] = []): string[] => {
  const o = d.options?.[key];
  return Array.isArray(o) && o.length ? o : fallback;
};
/** "Oct 31, 2026" from an ISO date — one copy, shared with the server (buttons.ts). */
export { dayWords };
const yes = (v: unknown) => (v === true ? 'Yes' : v === false ? 'No' : '—');

/** The building, "Name (cleaner)". */
export const buildingOf = (d: BuildingDraft) => d.display_name || d.facility_name || 'this building';

const FEEDBACK_SENTIMENTS_FALLBACK = ['Positive', 'Neutral', 'Negative', 'Critical'];
const DETAIL_LABEL: Record<string, string> = Object.fromEntries(
  Object.values(DETAIL_FIELD_SPECS).flat().map((s) => [s.key, s.label]),
);
const SUMMARY_LABEL: Record<string, string> = { facility_name: 'Facility name', facility_address: 'Address', market_region: 'Market' };
// The Security & Access card's own labels (facility-detail.tsx FacilityAccessCard).
export const ACCESS_LABEL: Record<string, string> = {
  alarm_code: 'Alarm code',
  arm_disarm_procedure: 'Arm / disarm',
  monitoring_company: 'Monitoring company',
  monitoring_phone: 'Monitoring phone',
  lock_type: 'Lock type',
  lock_code: 'Lock code',
  key_location: 'Key location',
  badge_issuer: 'Badge issuer',
  badge_obtain_procedure: 'Badge obtain procedure',
  other_access_notes: 'Other access notes',
  site_cheat_sheet: 'Site cheat sheet',
};
const ACCESS_LONG = new Set(['arm_disarm_procedure', 'badge_obtain_procedure', 'other_access_notes', 'site_cheat_sheet']);
// The Points of Contact window's own labels.
const CONTACT_LABEL: Record<string, string> = {
  preferred_name: 'Preferred name',
  first_name: 'First name',
  last_name: 'Last name',
  job_title: 'Job title at this building',
  phone: 'Phone at this building',
  email: 'Email at this building',
  role: 'Role at this building',
};

/**
 * The card for one draft, with what the person has typed into it. Pure: the
 * card renders it, the tests read it.
 */
export function buildingCard(d: BuildingDraft, f: Record<string, unknown>): CardSpec {
  const c = ctxOf(d);
  const here = buildingOf(d);
  const base: CardSpec = { title: '', fields: [], info: [], readBack: null, doItLabel: null, missing: null };
  const need = (cond: boolean, why: string) => (cond ? why : null);
  const words = () => buildingButtonWords(d.action, here, f, c);
  switch (d.action) {
    case 'log_feedback':
      return {
        ...base,
        title: 'Log feedback',
        fields: [
          { key: 'feedback_date', label: 'Date', kind: 'date', required: true },
          { key: 'sentiment', label: 'Sentiment', kind: 'select', required: true, options: opt(fromDraft(d, 'sentiment', FEEDBACK_SENTIMENTS_FALLBACK)) },
          { key: 'counts_against_no_miss', label: 'Counts against this month’s Scope Performance Payment', kind: 'boolean' },
          { key: 'counts_toward_compliment', label: 'Counts toward the quarterly Compliment', kind: 'boolean' },
          { key: 'description', label: 'Description', kind: 'textarea', required: true },
        ],
        missing: need(blank(f.description), 'Description is required.'),
      };
    case 'close_feedback': {
      const rows = ((c.rows ?? []) as Array<Record<string, unknown>>).map((r) => ({
        id: str(r.feedback_id),
        text: `${dayWords(r.feedback_date)} · ${str(r.sentiment)} · ${str(r.description).slice(0, 120)}`,
      }));
      const ids = (f.feedback_ids as string[] | undefined) ?? [];
      return {
        ...base,
        title: 'Close feedback',
        rows,
        rowsKey: 'feedback_ids',
        info: Number(c.left_off) > 0
          ? [{ label: 'Left off', value: `${c.left_off} more open item${Number(c.left_off) === 1 ? ' here is' : 's here are'} not yours to close — the building’s account manager can` }]
          : [],
        fields: [
          { key: 'new_status', label: 'Close as', kind: 'select', required: true, options: opt(['Resolved', 'Closed'], { Resolved: 'Resolved — it was fixed', Closed: 'Closed — no further action' }) },
          { key: 'resolution_text', label: 'What resolved it', kind: 'textarea', required: true },
        ],
        missing: ids.length === 0 ? 'Tick at least one item to close.' : need(blank(f.resolution_text), 'Say what resolved it.'),
      };
    }
    case 'log_communication':
      return {
        ...base,
        title: 'Log a conversation',
        fields: [
          { key: 'channel', label: 'How', kind: 'select', required: true, options: opt(['Phone_Call', 'Text_SMS', 'WhatsApp', 'Email', 'Google_Chat', 'In_Person', 'Other']) },
          { key: 'direction', label: 'Who reached out', kind: 'select', required: true, options: opt(['Inbound', 'Outbound', 'Internal'], { Inbound: 'They reached us', Outbound: 'We reached them', Internal: 'Inside TCS' }) },
          { key: 'contact_name', label: 'With', kind: 'text', required: true },
          { key: 'content', label: 'What was said', kind: 'textarea', required: true },
          { key: 'tone', label: 'How it went', kind: 'select', required: true, options: opt(['Positive', 'Neutral', 'Negative', 'Urgent']) },
          { key: 'communication_date', label: 'When', kind: 'datetime' },
        ],
        missing: need(blank(f.contact_name) || str(f.content).trim().length < 5, 'Say who it was with and what was said.'),
      };
    case 'set_rag': {
      const now = (c.current as Record<string, unknown> | undefined)?.rag;
      return {
        ...base,
        title: 'Change RAG',
        info: [{ label: 'Now', value: str(now) || 'Not set' }],
        fields: [
          { key: 'new_rag', label: 'Status', kind: 'select', required: true, options: opt(['Green', 'Amber', 'Red']) },
          { key: 'rag_category', label: 'Category', kind: 'select', options: opt(fromDraft(d, 'rag_category')) },
          { key: 'rag_reason', label: 'Reason', kind: 'textarea', required: true },
        ],
        missing: need(str(f.rag_reason).trim().length < 5, 'Add a reason.'),
      };
    }
    case 'edit_details': {
      const cur = (c.current ?? {}) as Record<string, unknown>;
      const markets = (c.markets as string[] | undefined) ?? [];
      const keys = Object.keys(d.fields);
      return {
        ...base,
        title: 'Edit building details',
        fields: keys.map((k) => {
          const label = SUMMARY_LABEL[k] ?? (k === 'sterile_sqft' ? 'Sterile sq ft' : DETAIL_LABEL[k] ?? k);
          const now = cur[k] == null || cur[k] === '' ? 'Not set' : str(cur[k]);
          if (k === 'market_region') return { key: k, label, kind: 'select' as const, required: true, options: opt(markets.length ? markets : [str(d.fields[k])]), now };
          if (k === 'facility_address') return { key: k, label, kind: 'textarea' as const, now };
          if (k === 'facility_name') return { key: k, label, kind: 'text' as const, required: true, now };
          if (k === 'emergency_dept_yn') return { key: k, label, kind: 'select' as const, options: opt(['Y', 'N'], { Y: 'Y — has an ED', N: 'N — no ED' }), now };
          return { key: k, label, kind: 'number' as const, now };
        }),
        missing: need('facility_name' in f && blank(f.facility_name), 'Facility name is required.'),
      };
    }
    case 'edit_access':
      return {
        ...base,
        title: 'Security & access',
        fields: Object.keys(d.fields).map((k) =>
          k === 'lock_type'
            ? { key: k, label: ACCESS_LABEL[k], kind: 'select' as const, options: opt(fromDraft(d, 'lock_type', [str(d.fields[k])])) }
            : { key: k, label: ACCESS_LABEL[k] ?? k, kind: ACCESS_LONG.has(k) ? ('textarea' as const) : ('text' as const) }),
      };
    case 'change_account_manager': {
      const people = (c.am_options as Array<{ eid: string; name: string }> | undefined) ?? [];
      const now = (c.current as Record<string, unknown> | undefined)?.account_manager;
      return {
        ...base,
        title: 'Change AM',
        info: [{ label: 'Now', value: str(now) || 'Unassigned' }],
        fields: [{ key: 'new_eid', label: 'New AM', kind: 'select', required: true, options: people.map((p) => ({ value: p.eid, label: p.name })) }],
        missing: need(blank(f.new_eid), 'An account manager is required — pick a person.'),
      };
    }
    case 'acknowledge_termination':
    case 'approve_termination':
    case 'set_termination_final_date':
    case 'cancel_termination': {
      const t = (c.termination ?? {}) as Record<string, unknown>;
      const finalDay = t.final_service_date ? dayWords(t.final_service_date) : 'no final date yet';
      const info = [
        { label: 'Termination', value: `${str(t.termination_id)} · last clean ${finalDay}` },
        { label: 'Signed off', value: `Finance ${yes(t.finance_ack)} · HR ${yes(t.hr_ack)} · Executive ${yes(t.approved)}` },
      ];
      if (d.action === 'acknowledge_termination') {
        const seat = d.fields.as === 'finance' ? 'Finance' : 'HR';
        return {
          ...base, title: `${seat} acknowledgement`, info,
          readBack: `This records ${seat}'s acknowledgement that ${here} stops being cleaned and billed after ${finalDay}.`,
          doItLabel: words(),
        };
      }
      if (d.action === 'approve_termination') {
        return {
          ...base, title: 'Final approval', info,
          readBack: `This gives the executive approval to end service at ${here}. The buildings close on ${finalDay}.`,
          doItLabel: words(),
        };
      }
      if (d.action === 'set_termination_final_date') {
        return {
          ...base, title: 'Set the final service date', info,
          fields: [{ key: 'final_service_date', label: 'Final service date (the last clean)', kind: 'date', required: true }],
          readBack: `This sets ${dayWords(f.final_service_date)} as the last clean at ${here}; the close runs that day.`,
          doItLabel: words(),
          missing: need(blank(f.final_service_date), 'A final service date is required — it is the date the close runs.'),
        };
      }
      return {
        ...base, title: 'Cancel the termination', info,
        readBack: `This cancels ${str(t.termination_id)}. Nothing closes, and ${here} stays in service.`,
        doItLabel: words(),
      };
    }
    case 'add_contact': {
      const existing = c.existing as { name?: string } | undefined;
      return {
        ...base,
        title: 'Add a contact',
        info: existing ? [{ label: 'Already on file', value: `${str(existing.name)} — this adds them to ${here} instead of making a second record.` }] : [],
        fields: existing
          ? [{ key: 'primary', label: 'Primary contact', kind: 'boolean' }]
          : [
              { key: 'first_name', label: 'First name', kind: 'text' },
              { key: 'last_name', label: 'Last name', kind: 'text' },
              { key: 'job_title', label: 'Job title', kind: 'text' },
              { key: 'role', label: 'Role', kind: 'text' },
              { key: 'phone', label: 'Phone', kind: 'text' },
              { key: 'email', label: 'Email', kind: 'text' },
              { key: 'primary', label: 'Primary contact', kind: 'boolean' },
            ],
        missing: existing ? null : need(blank(f.first_name) && blank(f.last_name) && blank(f.email), 'Enter at least a name or an email for the new person.'),
      };
    }
    case 'edit_contact': {
      const p = (c.contact ?? {}) as Record<string, unknown>;
      const cur = (p.current ?? {}) as Record<string, unknown>;
      return {
        ...base,
        title: 'Edit a contact',
        info: [{ label: 'Who', value: str(p.name) }],
        fields: Object.keys(d.fields).filter((k) => k !== 'person').map((k) => ({
          key: k, label: CONTACT_LABEL[k] ?? k, kind: 'text' as const, now: blank(cur[k]) ? 'Not set' : str(cur[k]),
        })),
      };
    }
    case 'set_contact_display': {
      const p = (c.contact ?? {}) as Record<string, unknown>;
      const fields: FieldSpec[] = [];
      if (d.fields.primary != null) fields.push({ key: 'primary', label: 'Primary contact', kind: 'boolean', now: yes(p.is_primary) });
      if (d.fields.shown != null) fields.push({ key: 'shown', label: 'Shown on the building card', kind: 'boolean', now: yes(p.is_displayed) });
      return { ...base, title: 'Primary or shown', info: [{ label: 'Who', value: str(p.name) }], fields };
    }
    case 'move_contact': {
      // The Points of Contact manager's up / down arrows: one place at a time.
      const p = (c.contact ?? {}) as Record<string, unknown>;
      const m = (c.move ?? {}) as Record<string, unknown>;
      const up = m.direction === 'up';
      return {
        ...base,
        title: 'Move a contact',
        info: [
          { label: 'Who', value: str(p.name) },
          { label: 'Now', value: `${str(m.position)} of ${str(m.of)} on the building card` },
          { label: 'Moves', value: `${up ? 'Up' : 'Down'} one place — ${up ? 'above' : 'below'} ${str(m.neighbour)}` },
        ],
      };
    }
    case 'remove_contact': {
      const p = (c.contact ?? {}) as Record<string, unknown>;
      return {
        ...base,
        title: 'Remove a contact',
        info: [{ label: 'Who', value: str(p.name) }, { label: 'How', value: `Takes them off ${here}. This is logged and can be undone.` }],
      };
    }
    case 'merge_contacts': {
      const p = (c.contact ?? {}) as Record<string, unknown>;
      const t = (c.target ?? {}) as Record<string, unknown>;
      const keep = `${str(t.name)}${t.email ? ` (${str(t.email)})` : t.phone ? ` (${str(t.phone)})` : ''}`;
      return {
        ...base,
        title: 'Merge two contacts',
        info: [{ label: 'Duplicate', value: str(p.name) }, { label: 'Keep', value: keep }],
        readBack: `This merges ${str(p.name)} into ${keep}: every building ${str(p.name)} is on moves to the record you keep, and the duplicate is hidden. Ask AI cannot undo it.`,
        doItLabel: words(),
      };
    }
    case 'add_document':
      return {
        ...base,
        title: 'Add a document',
        fields: [
          { key: 'document_type', label: 'Kind of document', kind: 'select', required: true, options: fromDraft(d, 'document_type', DOCUMENT_TYPES.map((t) => t.key)).map((k) => ({ value: k, label: DOCUMENT_TYPES.find((t) => t.key === k)?.label ?? k })) },
          { key: 'link', label: 'Link (Google Drive or other)', kind: 'text', required: true },
          { key: 'effective_date', label: 'Effective', kind: 'date' },
          { key: 'expiration_date', label: 'Expires', kind: 'date' },
        ],
        missing: need(!/^https?:\/\/\S+$/i.test(str(f.link).trim()), 'Paste the document’s link (it starts with https://).'),
      };
    case 'add_chemical':
    case 'edit_chemical':
    case 'remove_chemical': {
      const ch = (c.chemical ?? {}) as Record<string, unknown>;
      const cur = (ch.current ?? {}) as Record<string, unknown>;
      const managed = fromDraft(d, 'managed_by', ['TCS', 'Facility']);
      const supplierOpts = MANAGED_BY_CHOICES.filter((x) => x.value === '' || managed.includes(x.value)).map((x) => ({ value: x.value, label: x.label }));
      const info = [{ label: 'Chemical', value: `${str(ch.name)}${ch.off_list || ch.unmatched ? ' (not in the chemicals list — typed name)' : ''}` }];
      if (d.action === 'remove_chemical') {
        return { ...base, title: 'Remove a chemical', info: [...info, { label: 'How', value: 'Takes it off this building. Adding it back restores the same record.' }] };
      }
      return {
        ...base,
        title: d.action === 'add_chemical' ? 'Add a chemical' : 'Change a chemical',
        info,
        fields: [
          { key: 'managed_by', label: 'Who supplies it', kind: 'select', options: supplierOpts, now: d.action === 'edit_chemical' ? (str(cur.managed_by) || 'Not known') : undefined },
          { key: 'notes', label: 'Notes', kind: 'textarea', now: d.action === 'edit_chemical' ? (str(cur.notes) || 'None') : undefined },
        ],
      };
    }
    case 'confirm_map_pin':
      return {
        ...base,
        title: 'Map pin',
        fields: [
          { key: 'pin_result', label: 'Did this pin get you to the door?', kind: 'select', required: true, options: opt(['confirmed', 'wrong'], { confirmed: 'Yes', wrong: 'No, wrong spot' }) },
          { key: 'note', label: 'Note', kind: 'textarea' },
        ],
      };
    default:
      return { ...base, title: 'Building change', missing: 'This draft cannot be saved here — open the building.' };
  }
}

/** The card's starting values: the draft's fields, plus the AM's id for a change of AM. */
export function initialCardFields(d: BuildingDraft): Record<string, unknown> {
  const f = { ...(d.fields ?? {}) };
  if (d.action === 'change_account_manager') f.new_eid = (ctxOf(d).new_eid as string | undefined) ?? '';
  // The feedback form's default: today on the person's own device, never the
  // server's (UTC) date — after ~7 pm Central that was already tomorrow.
  if (d.action === 'log_feedback' && blank(f.feedback_date)) f.feedback_date = localToday();
  return f;
}

// ── Do it ────────────────────────────────────────────────────────────────────

export interface BuildingCommitResult {
  ok: boolean;
  error?: string;
  /** What happened, in words (the toast). */
  note?: string;
  target_table?: string;
  target_row_id?: string | null;
  committed_payload?: Record<string, unknown>;
  /** A close: how many really closed, of how many asked. */
  result?: { done: number; of: number };
}

const fail = (error: string): BuildingCommitResult => ({ ok: false, error });

/**
 * Save one building draft through the screen's own function. `f` is the card's
 * fields as the person left them; `eid` is the person's employee id.
 */
export async function commitBuildingAction(
  d: BuildingDraft,
  f: Record<string, unknown>,
  eid: string | null,
): Promise<BuildingCommitResult> {
  const fid = d.facility_id;
  const c = ctxOf(d);
  const here = buildingOf(d);
  const missing = buildingCard(d, f).missing;
  if (missing) return fail(missing);
  try {
    switch (d.action) {
      case 'log_feedback': {
        // The feedback form's save (feedback-form.tsx → saveFeedback).
        const id = await saveFeedback({
          facility_id: fid,
          account_id: d.account_id ?? null,
          feedback_date: str(f.feedback_date) || localToday(),
          reported_by_employee_id: eid,
          sentiment: str(f.sentiment),
          description: str(f.description),
          counts_against_nmi: f.counts_against_no_miss === true,
          counts_toward_compliment: f.counts_toward_compliment === true,
        });
        return { ok: true, target_table: 'facility_feedback', target_row_id: id, note: `Feedback logged at ${here}.`, committed_payload: { facility_id: fid, feedback_id: id, fields: f } };
      }
      case 'close_feedback': {
        // Ask AI is the only door that closes feedback (no screen does): the
        // same write it always made, read back so the card says what closed.
        const ids = (f.feedback_ids as string[] | undefined) ?? [];
        const patch = {
          status: str(f.new_status) || 'Resolved',
          resolution: str(f.resolution_text).trim(),
          resolved_by: eid,
          resolution_date: new Date().toISOString().split('T')[0],
        };
        const { data, error } = await supabase.from('facility_feedback').update(patch).eq('facility_id', fid).in('feedback_id', ids).select('feedback_id');
        if (error) return fail(rpcErrorMessage(error, 'Could not close these.'));
        const done = ((data ?? []) as Array<{ feedback_id: string }>).map((r) => r.feedback_id);
        const notClosed = ids.filter((id) => !done.includes(id));
        if (done.length === 0) return fail(`None of the ${ids.length} ${ids.length === 1 ? 'was' : 'were'} closed — the database did not let this sign-in close ${ids.length === 1 ? 'it' : 'them'}. The building's account manager can.`);
        return {
          ok: true,
          target_table: 'facility_feedback',
          target_row_id: null,
          result: { done: done.length, of: ids.length },
          note: notClosed.length
            ? `Closed ${done.length} of ${ids.length}. ${notClosed.length} ${notClosed.length === 1 ? 'was' : 'were'} not closed — the database did not let this sign-in close ${notClosed.length === 1 ? 'it' : 'them'}.`
            : `Closed ${done.length} feedback item${done.length === 1 ? '' : 's'} at ${here}.`,
          committed_payload: { facility_id: fid, target_row_ids: done, not_closed_ids: notClosed, patch },
        };
      }
      case 'log_communication': {
        // Ask AI is the only door to the communication log: its save, unchanged.
        if (!d.account_id) return fail(`no account for ${fid}`);
        const payload = {
          communication_id: `COM-${Math.random().toString(36).slice(2, 10).toUpperCase()}`,
          facility_id: fid,
          account_id: d.account_id,
          channel: f.channel,
          direction: f.direction,
          contact_name: str(f.contact_name).trim(),
          content: str(f.content).trim(),
          sentiment: f.tone,
          communication_date: str(f.communication_date) || new Date().toISOString(),
          logged_by: eid,
        };
        const { data, error } = await supabase.from('communication_log').insert(payload).select('communication_id').single();
        if (error || !data) return fail(error?.message ?? 'insert failed');
        const id = (data as { communication_id: string }).communication_id;
        return { ok: true, target_table: 'communication_log', target_row_id: id, note: `Conversation logged at ${here}.`, committed_payload: payload };
      }
      case 'set_rag': {
        // The RAG editors' save (rag-edit-modal / rag-edit-popover → saveRag).
        const { error } = await saveRag({
          facilityId: fid,
          rag: str(f.new_rag),
          category: str(f.rag_category) || null,
          reason: str(f.rag_reason).trim() || null,
          setByEid: eid,
        });
        if (error) return fail(error.message);
        return { ok: true, target_table: 'facilities', target_row_id: fid, note: `${here} is now ${str(f.new_rag)}.`, committed_payload: { facility_id: fid, fields: f } };
      }
      case 'edit_details': {
        // Account Summary (saveFieldsOnce, one statement) and Facility details
        // (planDetailsPatch + saveFacilityDetailsFields) — the two cards' saves.
        const summaryDraft: Record<string, string | number | null> = {};
        const detailDraft: Record<string, string | number | null> = {};
        // Square footage stays out of Ask AI (Ben 2026-10-21); the server never
        // drafts it, and this refuses a draft that carries it anyway.
        const sqft = Object.keys(f).filter((k) => SQFT_KEYS.includes(k));
        if (sqft.length) return fail('Square footage is changed on the building’s Info tab, not from Ask AI.');
        for (const [k, v] of Object.entries(f)) {
          if (k in SUMMARY_LABEL) summaryDraft[k] = v as string | null;
          else detailDraft[k] = v == null || v === '' ? null : (k === 'emergency_dept_yn' ? str(v) : Number(v));
        }
        const saved: string[] = [];
        const summary = summaryColumnsPatch(summaryDraft);
        if (Object.keys(summary).length) {
          const res = await saveFieldsOnce({ table: 'facilities', row_id: fid, pk_column: 'facility_id', fields: summary, label: 'save this building’s details' });
          if (!res.ok) return fail(res.error ?? 'Could not save this building’s details.');
          saved.push('Account Summary');
        }
        if (Object.keys(detailDraft).length) {
          const stored = (c.stored ?? {}) as Record<string, number | null>;
          const plan = planDetailsPatch({
            facilityType: (c.facility_type as string | null) ?? null,
            draft: detailDraft,
            storedTotal: stored.total_sqft ?? null,
            storedSterile: stored.sterile_sqft ?? null,
          });
          if (!plan.ok) return fail(saved.length ? `Saved: ${saved.join(', ')}. Not saved: Facility details — ${plan.error}` : plan.error);
          const res = await saveFacilityDetailsFields(fid, plan.patch);
          if (!res.ok) return fail(saved.length ? `Saved: ${saved.join(', ')}. Not saved: Facility details — ${res.error}` : res.error ?? 'Could not save the facility details.');
          saved.push('Facility details');
        }
        return { ok: true, target_table: 'facilities', target_row_id: fid, note: `${here}: ${saved.join(' and ')} saved.`, committed_payload: { facility_id: fid, fields: f } };
      }
      case 'edit_access': {
        // The Security & Access card's save (saveFieldsOnce on facility_access;
        // the first save creates the row, as the card does).
        const { data: row, error: rErr } = await supabase.from('facility_access').select('access_id').eq('facility_id', fid).maybeSingle();
        if (rErr) return fail(rErr.message);
        const fields: Record<string, string | null> = {};
        for (const [k, v] of Object.entries(f)) fields[k] = blank(v) ? null : str(v);
        const res = await saveFieldsOnce({
          table: 'facility_access',
          row_id: (row?.access_id as string | undefined) ?? null,
          pk_column: 'access_id',
          fields,
          label: 'save the security and access details',
          facility_id: fid,
        });
        if (!res.ok) return fail(res.error ?? 'Could not save the security and access details.');
        return { ok: true, target_table: 'facility_access', target_row_id: (row?.access_id as string | undefined) ?? res.new_row_id ?? null, note: `Security & access saved for ${here}.`, committed_payload: { facility_id: fid, fields: Object.keys(fields) } };
      }
      case 'change_account_manager': {
        const next = str(f.new_eid);
        const { error } = await assignFacilityAm(fid, next);
        if (error) return fail(error.message);
        const who = ((c.am_options as Array<{ eid: string; name: string }> | undefined) ?? []).find((p) => p.eid === next)?.name ?? next;
        return { ok: true, target_table: 'facilities', target_row_id: fid, note: `${who} is now the account manager of ${here}.`, committed_payload: { facility_id: fid, account_manager_eid: next } };
      }
      case 'acknowledge_termination':
      case 'approve_termination':
      case 'set_termination_final_date':
      case 'cancel_termination': {
        // The termination panel's steps (termination-record.tsx → terminationStep).
        const tid = str(((c.termination ?? {}) as Record<string, unknown>).termination_id);
        if (!tid) return fail('No termination is attached to this draft — open the Stop Service page.');
        let fn: 'rpc_ack_account_termination' | 'rpc_set_account_termination_final_date' | 'rpc_cancel_account_termination';
        let args: Record<string, unknown>;
        let note: string;
        if (d.action === 'acknowledge_termination' || d.action === 'approve_termination') {
          const step = d.action === 'approve_termination' ? 'approve' : d.fields.as === 'finance' ? 'finance' : 'hr';
          fn = 'rpc_ack_account_termination';
          args = { p_termination_id: tid, p_step: step };
          note = step === 'approve' ? 'Termination approved' : step === 'finance' ? 'Finance acknowledged' : 'HR acknowledged';
        } else if (d.action === 'set_termination_final_date') {
          fn = 'rpc_set_account_termination_final_date';
          args = { p_termination_id: tid, p_final_service_date: str(f.final_service_date) };
          note = 'Final service date set';
        } else {
          fn = 'rpc_cancel_account_termination';
          args = { p_termination_id: tid, p_reason: null };
          note = 'Termination cancelled — nothing will close';
        }
        await terminationStep(fn, args);
        return { ok: true, target_table: 'account_terminations', target_row_id: tid, note, committed_payload: { facility_id: fid, rpc: fn, args } };
      }
      case 'add_contact': {
        // The Points of Contact manager's adds (addExistingContact /
        // createContactForFacility), then its Set primary when asked.
        const existing = c.existing as { contact_id?: string } | undefined;
        let contactId: string | null = null;
        if (existing?.contact_id) {
          const { error } = await addExistingContact(fid, existing.contact_id, Number(c.next_order) || 1);
          if (error) return fail(error.message);
          contactId = existing.contact_id;
        } else {
          const res = await createContactForFacility({
            first: str(f.first_name), last: str(f.last_name), title: str(f.job_title), mobile: str(f.phone),
            email: str(f.email), role: str(f.role), scope: 'Facility', facilityId: fid, accountId: d.account_id ?? null,
          });
          if (res.error) return fail(res.error);
          contactId = res.contactId;
        }
        if (f.primary === true && contactId) {
          const { data: a, error: aErr } = await supabase
            .from('contact_assignments')
            .select('assignment_id, facility_id, account_id, is_primary')
            .eq('contact_id', contactId).eq('facility_id', fid).eq('is_removed', false).maybeSingle();
          if (aErr || !a) return fail(`Added, but could not make them primary: ${aErr?.message ?? 'their row here was not found'}`);
          const { error } = await setPrimaryContact(a as { assignment_id: string; facility_id: string | null; account_id: string | null; is_primary: boolean });
          if (error) return fail(`Added, but could not make them primary: ${error.message}`);
        }
        return { ok: true, target_table: 'contact_assignments', target_row_id: contactId, note: `Contact added to ${here}.`, committed_payload: { facility_id: fid, contact_id: contactId, existing: !!existing?.contact_id, primary: f.primary === true } };
      }
      case 'edit_contact': {
        // The Points of Contact card's save: the name on the person, the rest
        // on their row at this building (saveFieldsOnce, as the card does).
        const p = (c.contact ?? {}) as Record<string, unknown>;
        const person: Record<string, string | null> = {};
        const site: Record<string, string | null> = {};
        const SITE_COL: Record<string, string> = { job_title: 'job_title', phone: 'assignment_mobile', email: 'assignment_email', role: 'contact_role' };
        for (const [k, v] of Object.entries(f)) {
          if (k === 'person') continue;
          const val = blank(v) ? null : str(v).trim();
          if (k in SITE_COL) site[SITE_COL[k]] = val;
          else person[k] = val;
        }
        if (Object.keys(person).length) {
          const res = await saveFieldsOnce({ table: 'contacts', row_id: str(p.contact_id), pk_column: 'contact_id', fields: person, label: `save ${str(p.name)}’s name` });
          if (!res.ok) return fail(res.error ?? 'Could not save the name.');
        }
        if (Object.keys(site).length) {
          const res = await saveFieldsOnce({ table: 'contact_assignments', row_id: str(p.assignment_id), pk_column: 'assignment_id', fields: site, label: `save ${str(p.name)}’s details at this building` });
          if (!res.ok) return fail(Object.keys(person).length ? `Saved the name. Not saved: the details at this building — ${res.error}` : res.error ?? 'Could not save.');
        }
        return { ok: true, target_table: 'contact_assignments', target_row_id: str(p.assignment_id), note: `${str(p.name)} updated.`, committed_payload: { facility_id: fid, assignment_id: p.assignment_id, fields: Object.keys(f) } };
      }
      case 'set_contact_display': {
        // The Points of Contact manager's Show / Hide and Set primary.
        const p = (c.contact ?? {}) as Record<string, unknown>;
        const row = { assignment_id: str(p.assignment_id), facility_id: (p.facility_id as string | null) ?? null, account_id: (p.account_id as string | null) ?? null, is_primary: p.is_primary === true };
        if (f.shown != null && f.shown !== (p.is_displayed === true)) {
          const { error } = await patchContactAssignment(row.assignment_id, { is_displayed: f.shown === true });
          if (error) return fail(error.message);
        }
        if (f.primary != null && f.primary !== row.is_primary) {
          const { error } = await setPrimaryContact(row);
          if (error) return fail(error.message);
        }
        // ⛔ An UPDATE refused by RLS returns no error and no rows (TCSD-0168):
        // read it back, and say so when it did not land.
        const { data: after, error: rErr } = await supabase.from('contact_assignments').select('is_primary, is_displayed').eq('assignment_id', row.assignment_id).maybeSingle();
        if (rErr || !after) return fail(`Could not read the change back: ${rErr?.message ?? 'row not visible'}`);
        if ((f.primary != null && after.is_primary !== f.primary) || (f.shown != null && after.is_displayed !== f.shown)) {
          return fail('That did not save — the database did not let this sign-in change this contact.');
        }
        return { ok: true, target_table: 'contact_assignments', target_row_id: row.assignment_id, note: `${str(p.name)} updated.`, committed_payload: { facility_id: fid, assignment_id: row.assignment_id, primary: f.primary ?? null, shown: f.shown ?? null } };
      }
      case 'move_contact': {
        // The manager's arrows (contactSwapPlan + swapContactOrder), on the
        // building's contacts as they are now — the same rows the manager reads.
        const p = (c.contact ?? {}) as Record<string, unknown>;
        const dir = ((c.move ?? {}) as Record<string, unknown>).direction === 'up' ? -1 : 1;
        const { data: live, error: lErr } = await supabase
          .from('contact_assignments')
          .select('assignment_id, is_displayed, display_order')
          .or(`facility_id.eq.${fid},and(facility_id.is.null,account_id.eq.${d.account_id ?? '__none__'})`)
          .eq('is_removed', false);
        if (lErr) return fail(lErr.message);
        const plan = contactSwapPlan(
          ((live ?? []) as Array<{ assignment_id: string; is_displayed: boolean | null; display_order: number | null }>).map((r) => ({ ...r, is_displayed: r.is_displayed === true })),
          str(p.assignment_id),
          dir as -1 | 1,
        );
        if (!plan) return fail(`${str(p.name)} can no longer move that way — the contacts here changed. Open the building.`);
        const { error, landed } = await swapContactOrder(plan);
        if (error) return fail(error.message);
        if (landed < 2) return fail('That did not save — the database did not let this sign-in reorder these contacts.');
        return { ok: true, target_table: 'contact_assignments', target_row_id: str(p.assignment_id), note: `${str(p.name)} moved ${dir === -1 ? 'up' : 'down'}.`, committed_payload: { facility_id: fid, swap: { a: plan.a, b: plan.b } } };
      }
      case 'remove_contact': {
        const p = (c.contact ?? {}) as Record<string, unknown>;
        const { error } = await removeContactAssignment(str(p.assignment_id));
        if (error) return fail(error.message);
        return { ok: true, target_table: 'contact_assignments', target_row_id: str(p.assignment_id), note: `${str(p.name)} removed from ${here}.`, committed_payload: { facility_id: fid, assignment_id: p.assignment_id } };
      }
      case 'merge_contacts': {
        const p = (c.contact ?? {}) as Record<string, unknown>;
        const t = (c.target ?? {}) as Record<string, unknown>;
        const { error } = await mergeContactInto(str(p.contact_id), str(t.contact_id));
        if (error) return fail(error.message);
        return { ok: true, target_table: 'contacts', target_row_id: str(p.contact_id), note: `${str(p.name)} merged into ${str(t.name)}.`, committed_payload: { source_contact_id: p.contact_id, target_contact_id: t.contact_id } };
      }
      case 'add_document': {
        // The Documents card's Add or replace (fileFacilityDocument).
        const id = await fileFacilityDocument({
          facilityId: fid, type: str(f.document_type), link: str(f.link),
          effective: str(f.effective_date), expires: str(f.expiration_date),
        });
        return { ok: true, target_table: 'facility_documents', target_row_id: id, note: `Document filed on ${here}.`, committed_payload: { facility_id: fid, document_id: id, document_type: f.document_type } };
      }
      case 'add_chemical':
      case 'edit_chemical':
      case 'remove_chemical': {
        // The chemicals panel's saves (src/shared/buildings/facility-chemicals.ts).
        const ch = (c.chemical ?? {}) as Record<string, unknown>;
        const managedBy = (str(f.managed_by) || null) as ChemicalManagedBy | null;
        if (d.action === 'add_chemical') {
          const id = await addFacilityChemical({
            facilityId: fid,
            supplyId: ch.off_list ? null : str(ch.supply_id) || null,
            typedName: ch.off_list ? str(ch.name) : null,
            managedBy,
            notes: str(f.notes),
            byEid: eid,
            source: 'Manual',
          });
          return { ok: true, target_table: 'facility_chemicals', target_row_id: id, note: `${str(ch.name)} recorded at ${here}.`, committed_payload: { facility_id: fid, fc_id: id } };
        }
        if (d.action === 'edit_chemical') {
          // Only what the card carries is sent (TCSD-0072): a field the draft
          // does not hold is left as it is on the building, never blanked.
          await updateFacilityChemical({
            fcId: str(ch.fc_id),
            byEid: eid,
            ...('managed_by' in f ? { managedBy } : {}),
            ...('notes' in f ? { notes: str(f.notes) } : {}),
          });
          return { ok: true, target_table: 'facility_chemicals', target_row_id: str(ch.fc_id), note: `${str(ch.name)} updated.`, committed_payload: { facility_id: fid, fc_id: ch.fc_id } };
        }
        await removeFacilityChemical(str(ch.fc_id));
        return { ok: true, target_table: 'facility_chemicals', target_row_id: str(ch.fc_id), note: `${str(ch.name)} taken off ${here}.`, committed_payload: { facility_id: fid, fc_id: ch.fc_id } };
      }
      case 'confirm_map_pin': {
        const result = f.pin_result === 'wrong' ? 'wrong' : 'confirmed';
        const { error } = await reportCoordVerification(fid, result, str(f.note));
        if (error) return fail(errorMessage(error));
        return { ok: true, target_table: 'facilities', target_row_id: fid, note: result === 'confirmed' ? 'Pin confirmed on site.' : 'Pin reported wrong.', committed_payload: { facility_id: fid, result } };
      }
      default:
        return fail('This draft cannot be saved here — open the building.');
    }
  } catch (e) {
    return fail(rpcErrorMessage(e, errorMessage(e, 'Could not save this.')));
  }
}
